import React, { useState, useMemo, useEffect, useRef } from "react";
import { Search, Locate, X, LogIn, LogOut, User as UserIcon, Briefcase, Building2, Bell, Bookmark, Languages } from "lucide-react";
import { normalize, geocodeSearch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";

export default function Header({ jobs, onSearch, onLocateMe }) {
    const { user, login, logout } = useAuth();
    const { lang, t, toggle } = useLang();
    const [q, setQ] = useState("");
    const [focused, setFocused] = useState(false);
    const [geoResults, setGeoResults] = useState([]);
    const geoTimer = useRef(null);
    const [showMenu, setShowMenu] = useState(false);

    // Local suggestions (fast)
    const localSug = useMemo(() => {
        const nq = normalize(q).trim();
        if (!nq) return [];
        const seen = new Set();
        const out = [];
        for (const j of jobs) {
            const cand = [
                { type: "city", value: j.city, sub: j.state, target: { city: j.city, state: j.state, lat: j.lat, lng: j.lng } },
                { type: "title", value: j.title, sub: j.company, target: j },
                { type: "company", value: j.company, sub: j.loc, target: j },
            ];
            for (const c of cand) {
                const nv = normalize(c.value);
                if (!nv || !nv.includes(nq)) continue;
                const key = `${c.type}:${nv}`;
                if (seen.has(key)) continue;
                seen.add(key);
                out.push(c);
                if (out.length >= 6) break;
            }
            if (out.length >= 6) break;
        }
        return out;
    }, [q, jobs]);

    // Server-side geocode (debounced) — finds ANY city in US/CA even if we have no jobs there
    useEffect(() => {
        clearTimeout(geoTimer.current);
        const nq = q.trim();
        if (nq.length < 3) {
            setGeoResults([]);
            return;
        }
        geoTimer.current = setTimeout(async () => {
            const r = await geocodeSearch(nq);
            setGeoResults(r.slice(0, 4).map((g) => ({
                type: "place",
                value: (g.label || "").split(",").slice(0, 3).join(", "),
                sub: (g.label || "").split(",").slice(3).join(",").trim(),
                target: { lat: g.lat, lng: g.lng },
            })));
        }, 350);
        return () => clearTimeout(geoTimer.current);
    }, [q]);

    const suggestions = useMemo(() => {
        // Merge but avoid duplicate labels
        const seen = new Set();
        const out = [];
        for (const s of [...localSug, ...geoResults]) {
            const k = normalize(s.value);
            if (!k || seen.has(k)) continue;
            seen.add(k);
            out.push(s);
            if (out.length >= 8) break;
        }
        return out;
    }, [localSug, geoResults]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (suggestions.length > 0) {
            const s = suggestions[0];
            onSearch(s);
            setQ("");
        }
    };

    return (
        <header
            data-testid="app-header"
            className="fixed top-0 left-0 right-0 z-[400]"
        >
            <div className="mx-auto max-w-7xl px-2 md:px-6 pt-2 md:pt-4">
                <div className="glass-dark rounded-2xl md:rounded-3xl shadow-xl px-2.5 md:px-5 py-2 md:py-3 flex flex-row items-center gap-2 md:gap-3">
                    {/* Left: search + locate */}
                    <div className="flex items-center gap-1.5 md:gap-2 flex-1 min-w-0">
                        <form onSubmit={handleSubmit} className="relative flex-1 min-w-0">
                            <div className="relative flex items-center">
                                <Search className="absolute left-3 md:left-4 w-4 h-4 text-slate-400" />
                                <input
                                    data-testid="search-input"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    onFocus={() => setFocused(true)}
                                    onBlur={() => setTimeout(() => setFocused(false), 200)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Escape") {
                                            setQ("");
                                            e.currentTarget.blur();
                                        }
                                    }}
                                    placeholder={t("search.placeholder")}
                                    className="w-full pl-9 md:pl-11 pr-9 md:pr-10 py-2.5 md:py-3 rounded-xl md:rounded-2xl bg-[#1a1e2c]/70 border border-white/10 outline-none focus:border-[#e85812] focus:ring-4 focus:ring-[#e85812]/30 font-body text-[13px] md:text-[15px] text-white placeholder:text-slate-400"
                                />
                                {q && (
                                    <button
                                        type="button"
                                        onClick={() => setQ("")}
                                        className="absolute right-3 p-1 rounded-full hover:bg-white/10"
                                        data-testid="search-clear-btn"
                                        aria-label={t("search.clear")}
                                    >
                                        <X className="w-4 h-4 text-slate-300" />
                                    </button>
                                )}
                            </div>
                            {focused && suggestions.length > 0 && (
                                <div
                                    data-testid="search-suggestions"
                                    className="absolute left-0 right-0 mt-2 card-soft overflow-hidden animate-fade-up max-h-[70vh] overflow-y-auto"
                                >
                                    {suggestions.map((s, i) => (
                                        <button
                                            key={`${s.type}-${s.value}-${i}`}
                                            type="button"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => {
                                                onSearch(s);
                                                setQ("");
                                            }}
                                            className="w-full text-left px-4 py-3 hover:bg-[#fff2ea] border-b border-slate-100 last:border-b-0 flex items-center gap-3"
                                            data-testid={`search-suggestion-${i}`}
                                        >
                                            <span
                                                className={`text-[11px] font-display font-semibold uppercase tracking-wide px-2 py-0.5 rounded-md ${
                                                    s.type === "city" || s.type === "place"
                                                        ? "bg-[#ffdccb] text-[#e85812]"
                                                        : s.type === "title"
                                                          ? "bg-[#e6ecff] text-[#333A56]"
                                                          : "bg-slate-100 text-slate-600"
                                                }`}
                                            >
                                                {s.type === "place" ? "location" : s.type}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-body text-sm text-[#1a1e2c] truncate font-medium">
                                                    {s.value}
                                                </div>
                                                {s.sub && (
                                                    <div className="text-xs text-slate-500 truncate">{s.sub}</div>
                                                )}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {focused && q.trim() && suggestions.length === 0 && (
                                <div className="absolute left-0 right-0 mt-2 card-soft px-4 py-3 text-sm text-slate-500 animate-fade-up">
                                    {t("search.no_matches")}
                                </div>
                            )}
                        </form>
                        <button
                            type="button"
                            onClick={onLocateMe}
                            className="btn-ghost !py-2 md:!py-2.5 !px-2.5 md:!px-4 shrink-0"
                            data-testid="locate-me-btn"
                            aria-label={t("search.near_me")}
                            title={t("search.near_me")}
                        >
                            <Locate className="w-4 h-4" />
                            <span className="hidden md:inline">{t("search.near_me")}</span>
                        </button>
                        <button
                            type="button"
                            onClick={toggle}
                            className="btn-ghost !py-2 md:!py-2.5 !px-2.5 md:!px-3 shrink-0"
                            data-testid="lang-toggle-btn"
                            aria-label={t("lang.toggle.title")}
                            title={t("lang.toggle.title")}
                        >
                            <Languages className="w-4 h-4" />
                            <span className="font-display font-semibold text-[11px] md:text-[12px] tracking-wide uppercase">
                                {lang === "en" ? "ES" : "EN"}
                            </span>
                        </button>
                    </div>

                    {/* Right: employer CTA (desktop only) + auth */}
                    <div className="flex items-center gap-1.5 md:gap-3 md:pl-4 md:border-l md:border-white/10 shrink-0">
                        {user ? (
                            <div className="relative">
                                <button
                                    onClick={() => setShowMenu((s) => !s)}
                                    className="flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 border border-slate-200 hover:border-[#e85812] transition-colors"
                                    data-testid="user-menu-btn"
                                >
                                    {user.picture ? (
                                        <img
                                            src={user.picture}
                                            alt=""
                                            className="w-8 h-8 rounded-full object-cover"
                                            onError={(e) => (e.target.style.display = "none")}
                                        />
                                    ) : (
                                        <span className="w-8 h-8 rounded-full bg-[#ffdccb] text-[#e85812] flex items-center justify-center font-display font-bold text-sm">
                                            {(user.name || user.email)[0]?.toUpperCase()}
                                        </span>
                                    )}
                                    <span className="hidden md:block font-body text-[13px] text-[#1a1e2c] max-w-[120px] truncate">
                                        {user.name?.split(" ")[0] || user.email.split("@")[0]}
                                    </span>
                                </button>
                                {showMenu && (
                                    <div
                                        className="absolute right-0 mt-2 card-soft w-56 z-[560] py-1"
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid="user-menu"
                                    >
                                        <div className="px-4 py-3 border-b border-slate-100">
                                            <div className="font-display font-semibold text-sm text-[#1a1e2c] truncate">
                                                {user.name}
                                            </div>
                                            <div className="text-xs text-slate-500 truncate">
                                                {user.email}
                                            </div>
                                            {user.is_admin && (
                                                <span className="inline-block mt-1 text-[10px] font-display font-bold uppercase tracking-wide bg-[#ffdccb] text-[#e85812] px-1.5 py-0.5 rounded">
                                                    Admin
                                                </span>
                                            )}
                                        </div>
                                        {user.is_admin && (
                                            <button
                                                onClick={() => {
                                                    window.location.hash = "#leads";
                                                    setShowMenu(false);
                                                }}
                                                className="w-full text-left px-4 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2"
                                                data-testid="user-menu-admin"
                                            >
                                                <UserIcon className="w-3.5 h-3.5 text-[#e85812]" /> {t("menu.admin_panel")}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                window.location.hash = "#/my";
                                                setShowMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2"
                                            data-testid="user-menu-my-apps"
                                        >
                                            <Briefcase className="w-3.5 h-3.5 text-[#e85812]" /> {t("menu.my_apps")}
                                        </button>
                                        <button
                                            onClick={() => {
                                                window.location.hash = "#/alerts";
                                                setShowMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2"
                                            data-testid="user-menu-alerts"
                                        >
                                            <Bell className="w-3.5 h-3.5 text-[#e85812]" /> {t("menu.job_alerts")}
                                        </button>
                                        <button
                                            onClick={() => {
                                                window.location.hash = "#/saved";
                                                setShowMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2"
                                            data-testid="user-menu-saved"
                                        >
                                            <Bookmark className="w-3.5 h-3.5 text-[#e85812]" /> {t("menu.saved")}
                                        </button>
                                        <button
                                            onClick={() => {
                                                window.location.hash = "#/employer";
                                                setShowMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2"
                                            data-testid="user-menu-employer"
                                        >
                                            <Building2 className="w-3.5 h-3.5 text-[#e85812]" /> {t("menu.employer_portal")}
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await logout();
                                                setShowMenu(false);
                                            }}
                                            className="w-full text-left px-4 py-2 text-[13px] hover:bg-slate-50 flex items-center gap-2"
                                            data-testid="user-menu-logout"
                                        >
                                            <LogOut className="w-3.5 h-3.5" /> {t("menu.logout")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={() => (window.location.hash = "#/employer")}
                                    className="btn-ghost !py-2 !px-3 !text-xs md:!text-sm !hidden md:!inline-flex"
                                    data-testid="header-employer-btn"
                                    title={t("search.for_employers")}
                                >
                                    <Building2 className="w-4 h-4" />
                                    <span>{t("search.for_employers")}</span>
                                </button>
                                <button
                                    onClick={login}
                                    className="btn-ghost !py-2 !px-3 !text-xs md:!text-sm"
                                    data-testid="header-login-btn"
                                >
                                    <LogIn className="w-4 h-4" />
                                    <span className="hidden md:inline">{t("search.sign_in")}</span>
                                </button>
                            </>
                        )}
                        <img
                            src="/logo.png"
                            alt="Dailyworkjobs"
                            className="w-9 h-9 md:w-12 md:h-12 object-contain shrink-0"
                            data-testid="brand-logo"
                        />
                        <div className="min-w-0 hidden md:block">
                            <div className="font-display font-extrabold text-[17px] md:text-[19px] leading-tight text-white tracking-tight">
                                <span style={{ color: "#ffb488" }}>Daily</span>
                                <span style={{ color: "#ffffff" }}>work</span>
                                <span style={{ color: "#ffb488" }}>jobs</span>
                            </div>
                            <div className="text-[11.5px] md:text-xs text-slate-300 leading-tight max-w-[240px] md:max-w-none">
                                {t("brand.tagline")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
